export type BucketDateRangeFilterProperty =
  | 'StartTime'
  | 'CompletedTime'
  | 'Modified'
  | 'JobTimeWindow'

export type ResolvedBucketDateRange = [Date, Date]

export type BucketDateRangeBoundary =
  | { kind: 'Relative'; offsetInDays: number }
  | { kind: 'Absolute'; date: Date }

export interface BucketDateRangeLimits {
  /**
   * Minimum number of days allowed between the start and end dates.
   * Use null or undefined to indicate no lower bound.
   */
  minSpanDays?: number | null
  /**
   * Maximum number of days allowed between the start and end dates.
   * Use null or undefined to indicate no upper bound.
   */
  maxSpanDays?: number | null
  /**
   * Absolute earliest date a user may select. Null/undefined means all past dates are allowed.
   */
  minDate?: Date | null
  /**
   * Absolute latest date a user may select. Null/undefined means all future dates are allowed.
   */
  maxDate?: Date | null
}

export interface BucketDateRangePreset {
  key: string
  label: string
  /** Shorter label shown in the trigger button. Falls back to `label` when omitted. */
  triggerLabel?: string
  dividerBefore?: boolean
  range: {
    start: BucketDateRangeBoundary
    end: BucketDateRangeBoundary
  } | null
}

export interface BucketDateRangeFilterDefinition {
  property: BucketDateRangeFilterProperty
  type: 'DateTime'
  label: string
  limits?: BucketDateRangeLimits
  presets?: readonly BucketDateRangePreset[]
  /**
   * Optional preset key that should be applied by default when the bucket is selected.
   */
  defaultPresetKey?: string | null
}

export const BUCKET_DATE_FILTER_PREFIX = 'bucket_date_range__'

/**
 * Maximum date range supported by the back-end.  Any date filter ranges must
 * be equal to or less than this number.
 */
export const MAX_RANGE_SPAN = 365

export const PRESET_TODAY: BucketDateRangePreset = {
  key: 'today',
  label: 'Today',
  range: {
    start: { kind: 'Relative', offsetInDays: 0 },
    end: { kind: 'Relative', offsetInDays: 0 },
  },
}

export const PRESET_FUTURE_ANY: BucketDateRangePreset = {
  key: 'future_any',
  label: 'Any planned start date',
  triggerLabel: 'Any planned start',
  /**
   * Realistically, this is capped at 365 days including today, but we check
   * against the preset key when applying and null the range for this one so
   * that the range isn't indicated on the calendar.
   */
  range: null,
}

export const PRESET_FUTURE_2_DAYS: BucketDateRangePreset = {
  key: 'future_2_days',
  label: '1 day from now',
  range: {
    start: { kind: 'Relative', offsetInDays: 0 },
    end: { kind: 'Relative', offsetInDays: 1 },
  },
}

export const PRESET_FUTURE_7_DAYS: BucketDateRangePreset = {
  key: 'future_7_days',
  label: '1 week from now',
  range: {
    start: { kind: 'Relative', offsetInDays: 0 },
    end: { kind: 'Relative', offsetInDays: 6 },
  },
}

export const PRESET_FUTURE_30_DAYS: BucketDateRangePreset = {
  key: 'future_30_days',
  label: '1 month from now',
  range: {
    start: { kind: 'Relative', offsetInDays: 0 },
    end: { kind: 'Relative', offsetInDays: 29 },
  },
}

export const PRESET_PAST_2_DAYS: BucketDateRangePreset = {
  key: 'past_2_days',
  label: '1 day ago',
  range: {
    start: { kind: 'Relative', offsetInDays: -1 },
    end: { kind: 'Relative', offsetInDays: 0 },
  },
}

export const PRESET_PAST_7_DAYS: BucketDateRangePreset = {
  key: 'past_7_days',
  label: '1 week ago',
  range: {
    start: { kind: 'Relative', offsetInDays: -6 },
    end: { kind: 'Relative', offsetInDays: 0 },
  },
}

export const PRESET_PAST_30_DAYS: BucketDateRangePreset = {
  key: 'past_30_days',
  label: '1 month ago',
  range: {
    start: { kind: 'Relative', offsetInDays: -29 },
    end: { kind: 'Relative', offsetInDays: 0 },
  },
}

export const PRESET_PAST_90_DAYS: BucketDateRangePreset = {
  key: 'past_90_days',
  label: '3 months ago',
  range: {
    start: { kind: 'Relative', offsetInDays: -89 },
    end: { kind: 'Relative', offsetInDays: 0 },
  },
}

export const PRESET_PAST_365_DAYS: BucketDateRangePreset = {
  key: 'past_365_days',
  label: '1 year ago',
  range: {
    start: { kind: 'Relative', offsetInDays: -364 },
    end: { kind: 'Relative', offsetInDays: 0 },
  },
}
